import { useState, type ReactNode } from 'react'
import { DownloadSimple, MagnifyingGlass, Warning } from '@phosphor-icons/react'
import { isApiError } from '@/api'
import { Badge, Banner, Button, Input, useToast } from '@/ui'
import { errorMessage } from '../../api/core'
import { useRecallExport } from '../../api/recall'
import { recallReach, type RecallExport } from '../../model/recall/mappers'
import { accountedTone, pctText, seasonLabel } from '../../model/recall/lotBalance'
import { cleanRecallCode, validateRecallCode } from '../../model/recall/recallForm'
import { stepLabel, streamLabel, unitTypeLabel } from '../../model/recall/words'
import { ActionButton, Can } from '../../components/Can'
import { ConsolePage, QueryState } from '../../components/Layout'
import { Code, Facts, Kg, RefLink, Section, StatTile, Status, When } from '../../components/bits'
import { RecallDialog } from '../../components/recall/RecallDialog'
import { RecalledPackLots } from '../../components/recall/RecalledPackLots'
import { TraceBackPanel } from '../../components/recall/TraceBackPanel'
import { downloadFile } from '../../lib/download'
import { useUrlState } from '../../lib/useUrlState'

/** Recall — F7.9 mock recall and recall, F7-B1.b trace back samples. */
export function RecallPage() {
  const [f, setF] = useUrlState({ code: '', trace: '', rpage: 1 })
  const [text, setText] = useState(f.code)
  const [prev, setPrev] = useState(f.code)
  const [codeError, setCodeError] = useState<string | null>(null)
  if (f.code !== prev) {
    setPrev(f.code)
    setText(f.code)
  }
  const q = useRecallExport(f.code)
  const notOurs = q.isError && isApiError(q.error) && q.error.status === 404

  const run = () => {
    const err = validateRecallCode(text)
    setCodeError(err)
    if (err) return
    const code = cleanRecallCode(text)
    if (code === f.code) void q.refetch()
    else setF({ code })
  }

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Recall"
      description="From any pouch, carton, batch, lot or sack tag: every farmer lot, run, batch and pack lot it touched, and the customers who got pouches."
    >
      <form
        className="flex flex-wrap items-end gap-2 rounded-card border border-rule bg-surface px-4 py-3"
        onSubmit={(e) => {
          e.preventDefault()
          run()
        }}
      >
        <label className="flex min-w-[16rem] flex-1 flex-col gap-1">
          <span className="font-mono text-[10px] font-bold uppercase tracking-label text-muted">Pack lot, batch, lot or sack tag</span>
          <Input
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setCodeError(null)
            }}
            placeholder="Scan a pouch or type a code"
            leading={<MagnifyingGlass />}
            className="font-mono"
            invalid={Boolean(codeError)}
            data-autofocus
          />
        </label>
        <Button type="submit" loading={q.isFetching}>
          Run
        </Button>
        {codeError && <p className="w-full text-[13px] font-medium text-danger">{codeError}</p>}
      </form>

      {f.code &&
        (notOurs ? (
          <Banner tone="warn" title="Not found">
            {f.code} is not one of our codes. Check it, or scan the pouch again.
          </Banner>
        ) : (
          <QueryState query={q} label="the recall list">
            {q.data && <ExportView exp={q.data} onTrace={(trace) => setF({ trace })} />}
          </QueryState>
        ))}

      <Can perm={['lab-reports:read', 'stock:read']}>
        <TraceBackPanel code={f.trace} onCode={(trace) => setF({ trace })} />
      </Can>

      <Section title="Recalled pack lots" bodyClassName="p-0">
        <RecalledPackLots page={f.rpage} onPage={(rpage) => setF({ rpage })} />
      </Section>
    </ConsolePage>
  )
}

function ExportView({ exp, onTrace }: { exp: RecallExport; onTrace: (code: string) => void }) {
  const toast = useToast()
  const [recalling, setRecalling] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const reach = recallReach(exp)
  const openRuns = exp.runs.filter((r) => r.notClosed).length
  const farmerName = (id: string | null) => {
    const f = exp.farmers.find((x) => x.farmerId === id)
    return f ? (f.name ?? f.farmerCode ?? 'Farmer') : '—'
  }

  const download = async (format: 'json' | 'csv' | 'pdf') => {
    setBusy(format)
    try {
      await downloadFile('/recall-export', `recall-${exp.start.code}.${format}`, { code: exp.start.code, format })
    } catch (e) {
      toast.error('Could not download', errorMessage(e))
    } finally {
      setBusy(null)
    }
  }
  const canTrace = exp.start.unitType === 'BATCH' || exp.start.unitType === 'PACK'

  return (
    <>
      <Section
        title={
          <span className="flex flex-wrap items-center gap-2">
            <Code className="text-[inherit]">{exp.start.code}</Code>
            <Badge tone="neutral">{unitTypeLabel(exp.start.unitType)}</Badge>
          </span>
        }
        eyebrow="Mock recall"
        actions={
          <>
            {(['pdf', 'csv', 'json'] as const).map((fmt) => (
              <Button key={fmt} size="sm" variant="secondary" leadingIcon={<DownloadSimple />} loading={busy === fmt} onClick={() => void download(fmt)}>
                {fmt === 'pdf' ? 'PDF' : fmt === 'csv' ? 'Spreadsheet' : 'Data file'}
              </Button>
            ))}
            {canTrace && (
              <Can perm={['lab-reports:read', 'stock:read']}>
                <Button size="sm" variant="ghost" onClick={() => onTrace(exp.start.code)}>
                  Trace back samples
                </Button>
              </Can>
            )}
            <ActionButton perm="recalls:create" size="sm" variant="danger" leadingIcon={<Warning />} onClick={() => setRecalling(true)}>
              Recall
            </ActionButton>
          </>
        }
      >
        <Facts
          columns={4}
          items={[
            { label: 'Time taken', value: `${(exp.elapsedMs / 1000).toFixed(2)} s`, mono: true },
            { label: 'Made by', value: exp.generatedBy.name || exp.generatedBy.username },
            { label: 'Made at', value: <When at={exp.generatedAt} /> },
            { label: 'Pouches with customers', value: `${reach.pouchesOut} of ${reach.pouches}`, mono: true },
          ]}
        />
      </Section>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatTile label="Farmers" value={exp.farmers.length} />
        <StatTile label="Farmer lots" value={exp.lots.length} />
        <StatTile label="Runs" value={exp.runs.length} hint={openRuns > 0 ? `${openRuns} not closed` : undefined} tone={openRuns > 0 ? 'warn' : 'neutral'} />
        <StatTile label="Batches" value={exp.batches.length} hint={exp.otherOutputs.length ? `+ ${exp.otherOutputs.length} other outputs` : undefined} />
        <StatTile label="Pack lots" value={exp.packLots.length} />
        <StatTile label="Customers" value={reach.customers} tone={reach.customers > 0 ? 'info' : 'neutral'} />
      </div>

      <Section title="Customers who got pouches" eyebrow="Who to call" bodyClassName="p-0">
        <MiniTable
          caption="Customers"
          empty="No pouches from these pack lots have gone to a customer."
          head={['Customer', 'Pack lot', 'Invoice / challan', 'Pouches', 'Sent', 'Trip']}
          right={[3]}
          rows={exp.customers.map((c, i) => ({
            key: `${c.shipmentCode}-${c.packLotCode}-${i}`,
            cells: [c.org, <Code key="p">{c.packLotCode}</Code>, <Code key="c">{c.challanNo}</Code>, c.pouches, <When key="w" at={c.dispatchedAt} />, <Code key="t">{c.shipmentCode}</Code>],
          }))}
        />
      </Section>

      <Section title="Pack lots" eyebrow="Everything packed" bodyClassName="p-0">
        <MiniTable
          caption="Pack lots"
          empty="Nothing has been packed from this yet."
          head={['Pack lot', 'Pouches', 'With customers', 'At the hall', 'State', 'Hold']}
          right={[1, 2, 3]}
          rows={exp.packLots.map((p) => ({
            key: p.id,
            cells: [
              <RefLink key="l" to={`/c/pack-lots/${p.id}`}>{p.code}</RefLink>,
              p.pouches ?? '—',
              p.pouchesDispatched,
              Math.max((p.pouches ?? 0) - p.pouchesDispatched, 0),
              <Status key="s" status={p.status} />,
              <Status key="h" status={p.hold} />,
            ],
          }))}
        />
      </Section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Section title="Farmers and their lots" eyebrow="Back to the farm" bodyClassName="p-0">
          <MiniTable
            caption="Farmer lots"
            empty="No farmer lots found."
            head={['Lot', 'Farmer', 'Season', 'Kilos', 'State', 'Hold']}
            right={[3]}
            rows={exp.lots.map((l) => ({
              key: l.unitId,
              cells: [
                <RefLink key="l" to={`/c/lots/${l.unitId}`}>{l.code}</RefLink>,
                farmerName(l.farmerId),
                seasonLabel(l.seasonCode),
                <Kg key="k" grams={l.netG} />,
                <Status key="s" status={l.status} />,
                <Status key="h" status={l.hold} />,
              ],
            }))}
          />
        </Section>
        <Section title="Machine runs" eyebrow="Through the machines" bodyClassName="p-0">
          <MiniTable
            caption="Runs"
            empty="No runs."
            head={['Run', 'Step', 'State', 'Started', 'Ended']}
            rows={exp.runs.map((r) => ({
              key: r.id,
              cells: [
                <Code key="c">{r.code}</Code>,
                stepLabel(r.stepCode),
                <span key="s" className="flex flex-wrap items-center gap-1">
                  <Status status={r.status} />
                  {r.notClosed && <Badge tone="warn">Not closed</Badge>}
                </span>,
                <When key="a" at={r.startedAt} />,
                <When key="b" at={r.endedAt} />,
              ],
            }))}
          />
        </Section>
        <Section title="Batches" eyebrow="Made from these lots" bodyClassName="p-0">
          <MiniTable
            caption="Batches"
            empty="No batches."
            head={['Batch', 'Stream', 'Kilos', 'State', 'Hold']}
            right={[2]}
            rows={exp.batches.map((b) => ({
              key: b.unitId,
              cells: [<Code key="c">{b.code}</Code>, streamLabel(b.stream), <Kg key="k" grams={b.netG} />, <Status key="s" status={b.status} />, <Status key="h" status={b.hold} />],
            }))}
          />
        </Section>
        <Section title="Other outputs" eyebrow="By-product, waste, left over" bodyClassName="p-0">
          <MiniTable
            caption="Other outputs"
            empty="No other outputs."
            head={['Code', 'Kind', 'Stream', 'Kilos']}
            right={[3]}
            rows={exp.otherOutputs.map((o) => ({
              key: o.unitId,
              cells: [<Code key="c">{o.code}</Code>, unitTypeLabel(o.unitType), streamLabel(o.stream), <Kg key="k" grams={o.netG} />],
            }))}
          />
        </Section>
      </div>

      <Section title="Lot balance" eyebrow="Where the kilos went" bodyClassName="p-0">
        <MiniTable
          caption="Lot balance"
          empty="No farmer lot balance for this code."
          head={['Lot', 'Bought at the gate', 'Accounted for', 'Not accounted', 'Share']}
          right={[1, 2, 3, 4]}
          rows={exp.balance.map((b) => ({
            key: b.lotId,
            cells: [
              <RefLink key="l" to={`/c/lot-balance/${b.lotId}`}>{b.lotCode}</RefLink>,
              <Kg key="g" grams={b.gateNetG} />,
              <Kg key="a" grams={b.accountedG} />,
              <Kg key="x" grams={b.lostG} />,
              <span key="p" className={accountedTone(b) === 'danger' ? 'font-semibold text-danger' : accountedTone(b) === 'ok' ? 'text-ok' : undefined}>
                {pctText(b.accountedPct)}
              </span>,
            ],
          }))}
        />
      </Section>

      <RecallDialog exp={exp} open={recalling} onClose={() => setRecalling(false)} />
    </>
  )
}

function MiniTable({
  caption,
  head,
  rows,
  empty,
  right = [],
}: {
  caption: string
  head: string[]
  rows: Array<{ key: string; cells: ReactNode[] }>
  empty: string
  right?: number[]
}) {
  if (rows.length === 0) return <p className="px-4 py-3 text-[14px] text-muted">{empty}</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[14px]">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
            {head.map((h, i) => (
              <th key={h} scope="col" className={`whitespace-nowrap px-3 py-2 first:pl-4 last:pr-4 ${right.includes(i) ? 'text-right' : ''}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-rule/60">
              {r.cells.map((c, i) => (
                <td key={i} className={`px-3 py-2 first:pl-4 last:pr-4 ${right.includes(i) ? 'text-right font-mono tabular' : ''}`}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
